import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi, tiposAtividadeApi, veiculosApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  User, 
  Mail, 
  Shield, 
  Key,
  Loader2,
  Building,
  Pencil,
  Check,
  X,
  Plus,
  Trash2,
  GripVertical,
  ListChecks,
  Car,
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);
  
  // Edit name state
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  
  // Tipos de Atividade state
  const [tiposAtividade, setTiposAtividade] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [novoTipo, setNovoTipo] = useState({ nome: '', cor: '#f59e0b' });
  const [addingTipo, setAddingTipo] = useState(false);
  const [showAddTipo, setShowAddTipo] = useState(false);
  
  // Veículos state
  const [veiculos, setVeiculos] = useState([]);
  const [loadingVeiculos, setLoadingVeiculos] = useState(false);
  const [novoVeiculo, setNovoVeiculo] = useState({ nome: '', placa: '' });
  const [addingVeiculo, setAddingVeiculo] = useState(false);
  const [showAddVeiculo, setShowAddVeiculo] = useState(false);
  
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  
  useEffect(() => {
    if (isAdmin) {
      loadTiposAtividade();
      loadVeiculos();
    }
  }, [isAdmin]);
  
  const loadTiposAtividade = async () => {
    setLoadingTipos(true);
    try {
      const res = await tiposAtividadeApi.list(null); // todos, ativos e inativos
      setTiposAtividade(res.data || []);
    } catch (error) {
      console.error('Error loading tipos:', error);
    } finally {
      setLoadingTipos(false);
    }
  };
  
  const handleAddTipo = async () => {
    if (!novoTipo.nome.trim()) {
      toast.error('Digite o nome do tipo');
      return;
    }
    
    setAddingTipo(true);
    try {
      await tiposAtividadeApi.create(novoTipo);
      toast.success('Tipo de atividade criado!');
      setNovoTipo({ nome: '', cor: '#f59e0b' });
      setShowAddTipo(false);
      loadTiposAtividade();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar tipo';
      toast.error(msg);
    } finally {
      setAddingTipo(false);
    }
  };
  
  const handleToggleTipoAtivo = async (tipo) => {
    try {
      await tiposAtividadeApi.update(tipo.id, { ativo: !tipo.ativo });
      toast.success(tipo.ativo ? 'Tipo desativado' : 'Tipo ativado');
      loadTiposAtividade();
    } catch (error) {
      toast.error('Erro ao atualizar tipo');
    }
  };
  
  const handleUpdateTipoCor = async (tipo, novaCor) => {
    try {
      await tiposAtividadeApi.update(tipo.id, { cor: novaCor });
      loadTiposAtividade();
    } catch (error) {
      toast.error('Erro ao atualizar cor');
    }
  };

  // Funções de Veículos
  const loadVeiculos = async () => {
    setLoadingVeiculos(true);
    try {
      const res = await veiculosApi.list(null); // todos, ativos e inativos
      setVeiculos(res.data || []);
    } catch (error) {
      console.error('Error loading veiculos:', error);
    } finally {
      setLoadingVeiculos(false);
    }
  };
  
  const handleAddVeiculo = async () => {
    if (!novoVeiculo.nome.trim()) {
      toast.error('Digite o nome do veículo');
      return;
    }
    
    setAddingVeiculo(true);
    try {
      await veiculosApi.create(novoVeiculo);
      toast.success('Veículo criado!');
      setNovoVeiculo({ nome: '', placa: '' });
      setShowAddVeiculo(false);
      loadVeiculos();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar veículo';
      toast.error(msg);
    } finally {
      setAddingVeiculo(false);
    }
  };
  
  const handleToggleVeiculoAtivo = async (veiculo) => {
    try {
      await veiculosApi.update(veiculo.id, { ativo: !veiculo.ativo });
      toast.success(veiculo.ativo ? 'Veículo desativado' : 'Veículo ativado');
      loadVeiculos();
    } catch (error) {
      toast.error('Erro ao atualizar veículo');
    }
  };
  
  const handleUpdateVeiculoPlaca = async (veiculo, novaPlaca) => {
    try {
      await veiculosApi.update(veiculo.id, { placa: novaPlaca });
      loadVeiculos();
    } catch (error) {
      toast.error('Erro ao atualizar placa');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setChanging(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Senha alterada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao alterar senha';
      toast.error(message);
    } finally {
      setChanging(false);
    }
  };
  
  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Digite um nome válido');
      return;
    }
    
    setSavingName(true);
    try {
      await authApi.updateProfile({ name: newName.trim() });
      toast.success('Nome atualizado com sucesso!');
      setEditingName(false);
      // Refresh user data
      if (refreshUser) {
        refreshUser();
      }
      // Force page reload to update user context
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar nome';
      toast.error(message);
    } finally {
      setSavingName(false);
    }
  };
  
  const handleCancelEditName = () => {
    setNewName(user?.name || '');
    setEditingName(false);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Administrador';
      case 'viewer':
        return 'Visualizador';
      default:
        return 'Usuário';
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl space-y-6 animate-fade-in">
      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Meu Perfil
          </CardTitle>
          <CardDescription>
            Informações da sua conta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center text-white text-2xl font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="max-w-xs"
                    placeholder="Seu nome"
                    disabled={savingName}
                  />
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={handleSaveName}
                    disabled={savingName}
                  >
                    {savingName ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 text-green-600" />
                    )}
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={handleCancelEditName}
                    disabled={savingName}
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-slate-900">{user?.name}</h3>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => setEditingName(true)}
                  >
                    <Pencil className="w-3 h-3 text-slate-400" />
                  </Button>
                </div>
              )}
              <p className="text-sm text-slate-500">{getRoleLabel(user?.role)}</p>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium" data-testid="user-email">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Função</p>
                <p className="font-medium" data-testid="user-role">{getRoleLabel(user?.role)}</p>
              </div>
            </div>

            {user?.company_id && user?.company_name && (
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Empresa</p>
                  <p className="font-medium" data-testid="user-company">{user?.company_name}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Alterar Senha
          </CardTitle>
          <CardDescription>
            Atualize sua senha de acesso
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Senha Atual</Label>
              <Input
                id="current-password"
                type="password"
                data-testid="current-password-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Digite sua senha atual"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
              <Input
                id="confirm-password"
                type="password"
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a nova senha"
              />
            </div>

            <Button
              type="submit"
              data-testid="change-password-submit"
              disabled={changing}
              className="btn-primary"
            >
              {changing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Alterando...
                </>
              ) : (
                'Alterar Senha'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
      
      {/* Tipos de Atividade - apenas admin */}
      {isAdmin && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-slate-100">
              <ListChecks className="w-5 h-5 text-amber-500" />
              Tipos de Atividade
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Configure os tipos de atividade que aparecem nos dropdowns (Evento, Montagem, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingTipos ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Lista de tipos */}
                <div className="space-y-2">
                  {tiposAtividade.map((tipo) => (
                    <div 
                      key={tipo.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        tipo.ativo 
                          ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700' 
                          : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-600 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                        <input
                          type="color"
                          value={tipo.cor || '#f59e0b'}
                          onChange={(e) => handleUpdateTipoCor(tipo, e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border-0"
                          title="Clique para mudar a cor"
                        />
                        <span className={`font-medium ${tipo.ativo ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400 line-through'}`}>
                          {tipo.nome}
                        </span>
                        {!tipo.ativo && (
                          <Badge variant="outline" className="text-xs text-slate-400">Inativo</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={tipo.ativo}
                          onCheckedChange={() => handleToggleTipoAtivo(tipo)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Adicionar novo tipo */}
                {showAddTipo ? (
                  <div className="p-4 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-lg bg-amber-50/50 dark:bg-amber-900/10">
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={novoTipo.cor}
                        onChange={(e) => setNovoTipo({ ...novoTipo, cor: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={novoTipo.nome}
                        onChange={(e) => setNovoTipo({ ...novoTipo, nome: e.target.value })}
                        placeholder="Nome do novo tipo..."
                        className="flex-1 dark:bg-slate-800 dark:border-slate-600"
                      />
                      <Button 
                        onClick={handleAddTipo} 
                        disabled={addingTipo}
                        size="sm"
                      >
                        {addingTipo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setShowAddTipo(false);
                          setNovoTipo({ nome: '', cor: '#f59e0b' });
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full border-dashed dark:border-slate-600"
                    onClick={() => setShowAddTipo(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Tipo de Atividade
                  </Button>
                )}
                
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Tipos inativos não aparecem nos dropdowns de seleção.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Veículos da Empresa - apenas admin */}
      {isAdmin && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 dark:text-slate-100">
              <Car className="w-5 h-5 text-blue-500" />
              Veículos
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Configure os veículos da empresa (Ducato, Fiorino, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingVeiculos ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Lista de veículos */}
                <div className="space-y-2">
                  {veiculos.map((veiculo) => (
                    <div 
                      key={veiculo.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        veiculo.ativo 
                          ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700' 
                          : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-600 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                        <Car className="w-5 h-5 text-blue-500" />
                        <span className={`font-medium ${veiculo.ativo ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400 line-through'}`}>
                          {veiculo.nome}
                        </span>
                        {veiculo.placa && (
                          <Badge variant="outline" className="text-xs">{veiculo.placa}</Badge>
                        )}
                        {!veiculo.ativo && (
                          <Badge variant="outline" className="text-xs text-slate-400">Inativo</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          value={veiculo.placa || ''}
                          onChange={(e) => handleUpdateVeiculoPlaca(veiculo, e.target.value)}
                          placeholder="Placa"
                          className="w-28 h-8 text-xs dark:bg-slate-900 dark:border-slate-600"
                        />
                        <Switch
                          checked={veiculo.ativo}
                          onCheckedChange={() => handleToggleVeiculoAtivo(veiculo)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Adicionar novo veículo */}
                {showAddVeiculo ? (
                  <div className="p-4 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/10">
                    <div className="flex items-center gap-3">
                      <Car className="w-6 h-6 text-blue-500" />
                      <Input
                        value={novoVeiculo.nome}
                        onChange={(e) => setNovoVeiculo({ ...novoVeiculo, nome: e.target.value })}
                        placeholder="Nome do veículo (ex: HR Baú)"
                        className="flex-1 dark:bg-slate-800 dark:border-slate-600"
                      />
                      <Input
                        value={novoVeiculo.placa}
                        onChange={(e) => setNovoVeiculo({ ...novoVeiculo, placa: e.target.value })}
                        placeholder="Placa (opcional)"
                        className="w-32 dark:bg-slate-800 dark:border-slate-600"
                      />
                      <Button 
                        onClick={handleAddVeiculo} 
                        disabled={addingVeiculo}
                        size="sm"
                      >
                        {addingVeiculo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setShowAddVeiculo(false);
                          setNovoVeiculo({ nome: '', placa: '' });
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full border-dashed dark:border-slate-600"
                    onClick={() => setShowAddVeiculo(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Veículo
                  </Button>
                )}
                
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Veículos inativos não aparecem nos dropdowns de seleção.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
